import Alpine from "alpinejs";
import gsap from "gsap";
import { CustomEase } from "gsap/CustomEase";
import ScrollTrigger from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);
gsap.registerPlugin(CustomEase);

const HERBDORI_POP_EASE = CustomEase.create(
  "herbdoriPopEase",
  "M0,0 C0.12,0.72 0.22,1.18 0.42,1.08 C0.54,1.01 0.6,0.965 0.7,0.988 C0.8,1.012 0.9,1.004 1,1"
);

const getExistingElements = (
  elements: Array<HTMLElement | null | undefined>
): HTMLElement[] =>
  elements.filter(
    (element): element is HTMLElement => element instanceof HTMLElement
  );

const splitTextToChars = (
  element: HTMLElement,
  className = "herbdori-mv-text__char"
) => {
  const chars: HTMLElement[] = [];

  const splitNode = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || "").replace(/[\t\r\n]+/g, "").trim();

      if (!text) {
        return;
      }

      const fragment = document.createDocumentFragment();

      Array.from(text).forEach((char) => {
        const charElement = document.createElement("span");
        charElement.className = className;
        charElement.style.display = "inline-block";
        charElement.textContent = char === " " ? "\u00A0" : char;
        fragment.appendChild(charElement);
        chars.push(charElement);
      });

      node.parentNode?.replaceChild(fragment, node);
      return;
    }

    Array.from(node.childNodes).forEach(splitNode);
  };

  Array.from(element.childNodes).forEach(splitNode);

  return chars;
};

const playAfterTextReady = (callback: () => void) => {
  const playOnNextPaint = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  };

  if (document.fonts) {
    document.fonts.ready.then(playOnNextPaint).catch(playOnNextPaint);
    return;
  }

  playOnNextPaint();
};

const playHerbdoriTableFrameLoop = (
  tableFrame1: HTMLElement,
  tableFrame2: HTMLElement
) => {
  const showFrame = (activeFrame: HTMLElement) => {
    gsap.set([tableFrame1, tableFrame2], { opacity: 0 });
    gsap.set(activeFrame, { opacity: 1 });
  };

  gsap
    .timeline({
      repeat: -1,
    })
    .to({}, {
      duration: 1,
      ease: "steps(1)",
      onStart: () => {
        showFrame(tableFrame2);
      },
    })
    .to({}, {
      duration: 2,
      ease: "steps(1)",
      onStart: () => {
        showFrame(tableFrame1);
      },
    });
};

const getHerbdoriPopInner = (element: HTMLElement) =>
  element.querySelector<Element>("[data-herbdori-pop-inner]");

const setHerbdoriPopInnerInitialState = (innerElement: Element) => {
  const svgOrigin = innerElement.getAttribute("data-herbdori-pop-origin");

  gsap.set(innerElement, {
    scale: 1.18,
    ...(svgOrigin
      ? { svgOrigin }
      : { transformOrigin: "50% 50%" }),
  });
};

const createHerbdoriPopTimeline = (
  elements: HTMLElement[],
  trigger: HTMLElement,
  stagger = 0
) => {
  const timeline = gsap.timeline({
    scrollTrigger: {
      trigger,
      start: "top 80%",
      once: true,
    },
  });

  elements.forEach((element, index) => {
    const innerElement = getHerbdoriPopInner(element);
    const position = index * stagger;

    timeline
      .to(
        element,
        {
          opacity: 1,
          duration: 0.01,
          ease: "none",
        },
        position
      )
      .to(
        element,
        {
          scale: 1,
          duration: 1.65,
          ease: HERBDORI_POP_EASE,
          onComplete: () => {
            gsap.set(element, { willChange: "auto" });
          },
        },
        position
      );

    if (innerElement) {
      timeline.to(
        innerElement,
        {
          scale: 1,
          duration: 2.3,
          ease: HERBDORI_POP_EASE,
        },
        position + 0.1
      );
    }
  });
};

export const initHerbdoriMv = () => {
  Alpine.data("herbdoriMv", () => {
    return {
      init() {
        const name = this.$refs.name as HTMLElement | undefined;
        const table = this.$refs.table as HTMLElement | undefined;
        const tableFrame1 = this.$refs.tableFrame1 as HTMLElement | undefined;
        const tableFrame2 = this.$refs.tableFrame2 as HTMLElement | undefined;
        const copy = this.$refs.copy as HTMLElement | undefined;
        const image01 = this.$refs.image01 as HTMLElement | undefined;
        const image02 = this.$refs.image02 as HTMLElement | undefined;
        const figure = this.$refs.figure as HTMLElement | undefined;
        const text = this.$refs.text as HTMLElement | undefined;
        const root = this.$el as HTMLElement;
        const popGroups = Array.from(
          root.querySelectorAll<HTMLElement>("[data-herbdori-pop-group]")
        );
        const popElements = Array.from(
          root.querySelectorAll<HTMLElement>("[data-herbdori-pop-image]")
        );
        const nameElement = name instanceof HTMLElement ? name : null;
        const tableElement = table instanceof HTMLElement ? table : null;
        const tableFrame1Element =
          tableFrame1 instanceof HTMLElement ? tableFrame1 : null;
        const tableFrame2Element =
          tableFrame2 instanceof HTMLElement ? tableFrame2 : null;
        const introElements = getExistingElements([nameElement, tableElement]);
        const copyElement = copy instanceof HTMLElement ? copy : null;
        const photoElement = image01 instanceof HTMLElement ? image01 : null;
        const image02Element = image02 instanceof HTMLElement ? image02 : null;
        const figureElement = figure instanceof HTMLElement ? figure : null;
        const textElement = text instanceof HTMLElement ? text : null;
        const textParagraphs = textElement
          ? Array.from(textElement.querySelectorAll<HTMLElement>("p"))
          : [];
        const textCharGroups = textParagraphs.map((paragraph) =>
          splitTextToChars(paragraph)
        );
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        const isDesktop = window.matchMedia("(min-width: 750px)").matches;

        if (
          introElements.length === 0 &&
          copyElement === null &&
          photoElement === null &&
          image02Element === null &&
          figureElement === null &&
          popElements.length === 0 &&
          textElement === null
        ) {
          return;
        }

        if (nameElement) {
          gsap.set(nameElement, {
            opacity: 0,
            xPercent: -10,
            force3D: true,
            willChange: "transform, opacity",
          });
        }
        if (tableElement) {
          gsap.set(tableElement, {
            opacity: 0,
            xPercent: -5,
            force3D: true,
            willChange: "transform, opacity",
          });
        }
        if (tableFrame1Element && tableFrame2Element) {
          gsap.set(tableFrame1Element, { opacity: 1 });
          gsap.set(tableFrame2Element, { opacity: 0 });
        }
        if (copyElement) {
          gsap.set(copyElement, {
            opacity: 0,
            yPercent: 10,
            force3D: true,
            willChange: "transform, opacity",
          });
        }
        if (photoElement) {
          gsap.set(photoElement, {
            opacity: 0,
            scale: 0.9,
            transformOrigin: "50% 50%",
            force3D: true,
            willChange: "transform, opacity",
          });
        }
        if (image02Element) {
          gsap.set(image02Element, {
            opacity: 0,
            scale: 0.9,
            transformOrigin: "50% 50%",
            force3D: true,
            willChange: "transform, opacity",
          });
        }
        if (figureElement) {
          gsap.set(figureElement, {
            opacity: 0,
            yPercent: 10,
            force3D: true,
            willChange: "transform, opacity",
          });
        }
        popElements.forEach((popElement) => {
          const innerElement = getHerbdoriPopInner(popElement);

          gsap.set(popElement, {
            opacity: 0,
            scale: 0.82,
            transformOrigin: "50% 50%",
            force3D: true,
            willChange: "transform, opacity",
          });

          if (innerElement) {
            setHerbdoriPopInnerInitialState(innerElement);
          }
        });
        gsap.set(textParagraphs, {
          opacity: 0,
        });
        textCharGroups.forEach((chars) => {
          gsap.set(chars, {
            opacity: 0.2,
          });
        });

        if (prefersReducedMotion) {
          gsap.set(
            getExistingElements([
              ...introElements,
              copyElement,
              photoElement,
              image02Element,
              figureElement,
              ...popElements,
            ]),
            {
              opacity: 1,
              xPercent: 0,
              yPercent: 0,
              scale: 1,
            }
          );
          popElements.forEach((popElement) => {
            const innerElement = getHerbdoriPopInner(popElement);

            if (innerElement) {
              gsap.set(innerElement, { scale: 1 });
            }
          });
          gsap.set(textParagraphs, { opacity: 1 });
          textCharGroups.forEach((chars) => {
            gsap.set(chars, { opacity: 1 });
          });
          return;
        }

        const introTimeline = gsap.timeline({ paused: true });

        introTimeline.to(introElements, {
          opacity: 1,
          xPercent: 0,
          duration: 1.2,
          ease: "power2.inOut",
          stagger: 0.08,
          onComplete: () => {
            gsap.set(introElements, { willChange: "auto" });
            if (tableFrame1Element && tableFrame2Element) {
              playHerbdoriTableFrameLoop(
                tableFrame1Element,
                tableFrame2Element
              );
            }
          },
        });

        if (copyElement) {
          introTimeline.to(
            copyElement,
            {
              opacity: 1,
              yPercent: 0,
              duration: 1.2,
              ease: "power2.out",
              onComplete: () => {
                gsap.set(copyElement, { willChange: "auto" });
              },
            },
            "-=0.35"
          );
        }

        if (photoElement && isDesktop) {
          gsap.to(photoElement, {
            opacity: 1,
            scale: 1,
            duration: 1.1,
            ease: "back.out(1.4)",
            onComplete: () => {
              gsap.set(photoElement, { willChange: "auto" });
            },
            scrollTrigger: {
              trigger: photoElement,
              start: "top 80%",
              once: true,
            },
          });
        }

        if (photoElement && !isDesktop) {
          introTimeline.to(photoElement, {
            opacity: 1,
            scale: 1,
            duration: 1.1,
            ease: "back.out(1.4)",
            onComplete: () => {
              gsap.set(photoElement, { willChange: "auto" });
            },
          });
        }

        if (image02Element) {
          gsap.to(image02Element, {
            opacity: 1,
            scale: 1,
            duration: 1.1,
            ease: "back.out(1.4)",
            onComplete: () => {
              gsap.set(image02Element, { willChange: "auto" });
            },
            scrollTrigger: {
              trigger: image02Element,
              start: "top 80%",
              once: true,
            },
          });
        }

        if (figureElement) {
          gsap.to(figureElement, {
            opacity: 1,
            yPercent: 0,
            duration: 1.2,
            ease: "power2.out",
            onComplete: () => {
              gsap.set(figureElement, { willChange: "auto" });
            },
            scrollTrigger: {
              trigger: figureElement,
              start: "top 80%",
              once: true,
            },
          });
        }

        const groupedPopElements = new Set<HTMLElement>();
        popGroups.forEach((group) => {
          const groupElements = Array.from(
            group.querySelectorAll<HTMLElement>("[data-herbdori-pop-image]")
          );

          groupElements.forEach((element) => groupedPopElements.add(element));

          if (groupElements.length > 0) {
            createHerbdoriPopTimeline(groupElements, group, 0.14);
          }
        });

        popElements
          .filter((element) => !groupedPopElements.has(element))
          .forEach((element) => {
            createHerbdoriPopTimeline([element], element);
          });

        textParagraphs.forEach((paragraph, index) => {
          const chars = textCharGroups[index];
          const paragraphTimeline = gsap.timeline({
            scrollTrigger: {
              trigger: paragraph,
              start: "top 95%",
              once: true,
            },
          });

          paragraphTimeline
            .to(paragraph, {
              opacity: 1,
              duration: 0.01,
              ease: "none",
            })
            .to(chars, {
              opacity: 1,
              duration: 1.1,
              ease: "power2.inOut",
              stagger: 0.02,
            });
        });

        playAfterTextReady(() => {
          introTimeline.play();
        });
      },
    };
  });
};
